<!--
  One job site: its fields, and — once it exists — the five form lists filed
  under it, each with a New button that already knows the site. Finish /
  reopen is a status toggle; there is no delete here (admin, empty sites
  only). Follows the other quality forms: raw state, explicit Save, no
  modals.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { linkHandler, navigate, routePath } from '../../lib/router';
  import { notify, notifyError } from '../../lib/store';
  import type { SaveSiteRequest, SiteRecord, SiteResponse } from '../../lib/types';
  import SiteFields from './SiteFields.svelte';

  interface Props {
    /** null for a new site. */
    siteId?: number | null;
  }
  let { siteId = null }: Props = $props();

  let site = $state<SaveSiteRequest>({ name: '', street: '', city: '', province: '', postal_code: '', start_date: '', end_date: '', notes: '', bills_concrete: true });
  let status = $state<'active' | 'finished'>('active');
  let page = $state<SiteResponse | null>(null);
  let loading = $state(siteId !== null);
  let saving = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});
  let savedId = $state<number | null>(siteId);
  /** Awaiting the inline Yes/No before a rename that would touch linked forms. */
  let confirmRename = $state(false);
  /** Site details are collapsed on an existing site; the forms are what the day is about. */
  let detailsOpen = $state(false);
  /** The five form cards start collapsed: title, count and New. Tap to see the records. */
  let openCards = $state<Record<string, boolean>>({});

  /** One line under the name: address · dates. */
  const summary = $derived.by(() => {
    const where = [site.street, site.city, site.province].filter((b) => (b ?? '').trim() !== '').join(', ');
    const when = site.start_date ? `${site.start_date}${site.end_date ? ` – ${site.end_date}` : ' →'}` : '';
    return [where || 'No address yet', when].filter(Boolean).join(' · ');
  });

  /** Lucide-style outline icons, one per form type. */
  const ICONS: Record<string, string> = {
    'Job Hazard Analysis': 'M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
    'Concrete Field Testing': 'M9 3h6m-5 0v6.5L4.5 19a2 2 0 0 0 1.7 3h11.6a2 2 0 0 0 1.7-3L14 9.5V3',
    'Equipment Safety Inspection': 'M12 2 3 7v6c0 5 4 8 9 9 5-1 9-4 9-9V7l-9-5Zm-3 10 2 2 4-4',
    'Daily Report': 'M8 2v3m8-3v3M4 8h16M6 4h12a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
    'Contract Report': 'M4 4h16v16H4z M8 9h8 M8 13h5 M8 17h8',
    'Cement inventory': 'M4 20V9l8-5 8 5v11M4 14h16M9 20v-6m6 6v-6'
  };

  async function load(id: number) {
    loading = true;
    try {
      page = await api.getSite(id);
      const s = page.site;
      site = { name: s.name, street: s.street, city: s.city, province: s.province, postal_code: s.postal_code, start_date: s.start_date, end_date: s.end_date, notes: s.notes, bills_concrete: s.bills_concrete === 1 };
      status = s.status;
    } catch (cause) {
      notifyError(cause, 'Could not load that site');
      error = 'Could not load that site.';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    if (siteId !== null) void load(siteId);
  });

  /** Every form the rename cascade would rewrite the job name on. */
  const linkedFormCount = $derived(
    page === null ? 0 : page.jha.length + page.field_tests.length + page.equipment.length + page.daily_reports.length
  );

  /** Save directly, unless this Save would rename a site with linked forms — then ask first. */
  function requestSave() {
    const newName = (site.name ?? '').trim();
    const renaming = savedId !== null && page !== null && newName !== '' && newName !== page.site.name;
    if (renaming && linkedFormCount > 0) {
      confirmRename = true;
      return;
    }
    void save();
  }

  async function save() {
    if (saving) return;
    confirmRename = false;
    saving = true;
    error = null;
    fieldErrors = {};
    try {
      const saved = savedId === null ? await api.createSite(site) : await api.updateSite(savedId, site);
      const isNew = savedId === null;
      savedId = saved.id;
      notify({ tone: 'ok', title: isNew ? 'Site created' : 'Site saved' });
      if (isNew) navigate(routePath.qualitySite(saved.id), { replace: true, keepScroll: true });
      else await load(saved.id);
    } catch (cause) {
      if (isApiError(cause)) { error = cause.message; fieldErrors = cause.fields ?? {}; }
      else error = 'Could not save. Check the connection and try again.';
    } finally {
      saving = false;
    }
  }

  async function setStatus(next: 'active' | 'finished') {
    if (savedId === null) return;
    try {
      await api.updateSite(savedId, { status: next });
      status = next;
      notify({ tone: 'ok', title: next === 'finished' ? 'Site marked finished' : 'Site reopened' });
    } catch (cause) {
      notifyError(cause, 'Could not change the site status');
    }
  }

  const sections = $derived(
    page === null || savedId === null
      ? []
      : [
          { title: 'Job Hazard Analysis', records: page.jha, open: (r: SiteRecord) => routePath.qualityJha(r.id), all: routePath.qualityJhaList(savedId), add: routePath.newQualityJha(savedId), noun: 'JHA' },
          { title: 'Concrete Field Testing', records: page.field_tests, open: (r: SiteRecord) => routePath.qualityFieldTest(r.id), all: routePath.qualityFieldTestList(savedId), add: routePath.newQualityFieldTest(savedId), noun: 'field test log' },
          { title: 'Equipment Safety Inspection', records: page.equipment, open: (r: SiteRecord) => routePath.qualityEquipment(r.id), all: routePath.qualityEquipmentList(savedId), add: routePath.newQualityEquipment(savedId), noun: 'inspection' },
          { title: 'Daily Report', records: page.daily_reports, open: (r: SiteRecord) => routePath.qualityDailyReport(r.id), all: routePath.qualityDailyReportList(savedId), add: routePath.newQualityDailyReport(savedId), noun: 'daily report' },
          { title: 'Contract Report', records: page.contract_reports, open: (r: SiteRecord) => routePath.qualityContractReport(r.id), all: routePath.qualityContractReportList(savedId), add: routePath.newQualityContractReport(savedId), noun: 'contract report' }
        ]
  );
</script>

<a class="back" href={routePath.quality()} onclick={linkHandler}>
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 6-6 6 6 6" /></svg>
  All sites
</a>

{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else}
  <!--
    Site details: collapsed on an existing site, open on a new one. The
    header row carries what someone actually needs at a glance — name, where,
    when, status — and the fields sit behind a chevron.
  -->
  <section class="details">
    <div class="details__head">
      <span class="details__rail" class:details__rail--done={status === 'finished'} aria-hidden="true"></span>
      <div class="details__title">
        <h1>{savedId === null ? 'New site' : site.name || 'Site'}</h1>
        {#if savedId !== null}<p class="details__summary">{summary}</p>{/if}
      </div>
      {#if savedId !== null}
        <span class="status" class:status--done={status === 'finished'}>{status}</span>
        <button class="details__toggle" type="button" aria-expanded={detailsOpen} onclick={() => (detailsOpen = !detailsOpen)}>
          <svg viewBox="0 0 24 24" aria-hidden="true" class:open={detailsOpen}><path d="m6 9 6 6 6-6" /></svg>
          <span>{detailsOpen ? 'Hide details' : 'Site details'}</span>
        </button>
      {/if}
    </div>

    {#if detailsOpen || savedId === null}
      <div class="details__body">
        <SiteFields bind:site {fieldErrors} />
        {#if error}<p class="field__flag">{error}</p>{/if}
        <div class="btnrow">
          {#if confirmRename}
            <span class="nowrap">
              Renaming rewrites the job name on {linkedFormCount} linked forms (printed sheets included). Rename?
              <button class="btn btn--primary btn--sm" type="button" onclick={() => void save()}>Yes</button>
              <button class="btn btn--sm" type="button" onclick={() => (confirmRename = false)}>No</button>
            </span>
          {:else}
            <button class="btn btn--primary" type="button" disabled={saving} onclick={requestSave}>{savedId === null ? 'Create site' : 'Save'}</button>
          {/if}
          {#if savedId !== null}
            {#if status === 'active'}
              <button class="btn" type="button" onclick={() => void setStatus('finished')}>Mark finished</button>
            {:else}
              <button class="btn" type="button" onclick={() => void setStatus('active')}>Reopen</button>
            {/if}
          {/if}
        </div>
      </div>
    {/if}
  </section>

  {#if sections.length > 0}
    <div class="forms">
      {#each sections as section, i (section.title)}
        <section class="form" style="--i: {i}">
          <div class="form__head">
            <button
              class="form__toggle"
              type="button"
              aria-expanded={!!openCards[section.title]}
              onclick={() => (openCards[section.title] = !openCards[section.title])}
            >
              <span class="form__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICONS[section.title]} /></svg></span>
              <span class="form__title">
                <h2>{section.title}</h2>
                <span class="form__count">{section.records.length} {section.records.length === 1 ? 'record' : 'records'}</span>
              </span>
              <svg class="form__chev" class:open={!!openCards[section.title]} viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            {#if status === 'finished'}
              <span class="meta form__closed">Reopen to add</span>
            {:else}
              <a class="form__new" href={section.add} onclick={linkHandler}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                New
              </a>
            {/if}
          </div>
          {#if !openCards[section.title]}
            <!-- collapsed: the head says it all -->
          {:else if section.records.length === 0}
            <p class="form__empty">None yet.</p>
          {:else}
            <ul class="form__list">
              {#each section.records.slice(0, 4) as r (r.id)}
                <li>
                  <a href={section.open(r)} onclick={linkHandler}>
                    <span class="form__label">{r.label || 'Untitled'}</span>
                    <span class="form__date">{r.date || r.updated_at.slice(0, 10)}</span>
                  </a>
                </li>
              {/each}
            </ul>
            {#if section.records.length > 4}
              <a class="form__all" href={section.all} onclick={linkHandler}>All {section.records.length} →</a>
            {/if}
          {/if}
        </section>
      {/each}

      <!--
        Not a form type: nothing is filed under it. The cement inventory is
        read off the Daily and Contract Reports above, so it sits with them as
        a link to the ledger rather than a list of records.
      -->
      <a class="form form--link" style="--i: {sections.length}" href={routePath.qualityCement(savedId as number)} onclick={linkHandler}>
        <span class="form__head">
          <span class="form__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d={ICONS['Cement inventory']} /></svg></span>
          <span class="form__title">
            <h2>Cement inventory</h2>
            <span class="form__count">Deliveries in, pours out, what is in the silo</span>
          </span>
          <svg class="form__chev form__chev--go" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        </span>
      </a>
    </div>
  {/if}
{/if}

<style>
  .back {
    display: inline-flex; align-items: center; gap: 0.25rem;
    min-height: var(--tap); padding: 0 0.2rem; margin-bottom: 0.4rem;
    color: var(--ink-soft); font-weight: 700; text-decoration: none;
  }
  .back svg { width: 1.1rem; height: 1.1rem; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; }

  /* ---- details --------------------------------------------------------- */
  .details {
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    margin-bottom: 0.9rem;
    overflow: hidden;
  }
  .details__head {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-areas: 'rail title status' 'rail title toggle';
    align-items: center;
    gap: 0.2rem 0.8rem;
    padding: 0.9rem 0.9rem 0.9rem 0;
  }
  .details__rail { grid-area: rail; align-self: stretch; width: 6px; background: var(--accent); margin-right: 0.6rem; }
  .details__rail--done { background: var(--line); }
  .details__title { grid-area: title; min-width: 0; }
  .details__title h1 { margin: 0; font-size: 1.6rem; letter-spacing: -0.02em; line-height: 1.1; }
  .details__summary { margin: 0.2rem 0 0; font-size: 0.88rem; color: var(--ink-soft); }
  .status {
    grid-area: status; justify-self: end;
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    padding: 0.25rem 0.6rem; border-radius: 999px;
    background: var(--ok-tint); color: var(--ok);
  }
  .status--done { background: var(--pending-tint); color: var(--pending); }
  .details__toggle {
    grid-area: toggle; justify-self: end;
    display: inline-flex; align-items: center; gap: 0.3rem;
    min-height: var(--tap); padding: 0 0.4rem;
    border: 0; background: transparent; font: inherit; font-weight: 700; color: var(--brand); cursor: pointer;
  }
  .details__toggle svg { width: 1.1rem; height: 1.1rem; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; transition: transform 200ms ease-out; }
  .details__toggle svg.open { transform: rotate(180deg); }
  .details__body { padding: 0.9rem 1rem 1rem; border-top: 1px solid var(--line-soft); }

  @media (min-width: 48rem) {
    .details__head { grid-template-columns: auto 1fr auto auto; grid-template-areas: 'rail title status toggle'; }
  }

  /* ---- the five form cards ----------------------------------------------- */
  .forms { display: grid; grid-template-columns: 1fr; gap: 0.75rem; }
  @media (min-width: 40rem) { .forms { grid-template-columns: 1fr 1fr; } }

  .form {
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    padding: 0.9rem;
    display: flex; flex-direction: column; gap: 0.5rem;
    animation: rise 220ms ease-out both;
    animation-delay: calc(var(--i, 0) * 40ms);
  }
  @keyframes rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

  .form__head { display: flex; align-items: center; gap: 0.7rem; }
  .form__icon {
    flex: 0 0 auto; width: 2.4rem; height: 2.4rem; border-radius: 0.7rem;
    display: grid; place-items: center; background: var(--brand-tint); color: var(--brand);
  }
  .form__icon svg { width: 1.3rem; height: 1.3rem; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  .form__toggle {
    flex: 1 1 auto; min-width: 0;
    display: flex; align-items: center; gap: 0.7rem; text-align: left;
    padding: 0; border: 0; background: transparent; font: inherit; color: inherit; cursor: pointer;
    min-height: var(--tap);
  }
  .form__toggle:focus-visible { outline: 3px solid var(--brand); outline-offset: 2px; border-radius: var(--radius); }
  .form__title { flex: 1 1 auto; min-width: 0; display: grid; }
  .form__chev { flex: 0 0 auto; width: 1.2rem; height: 1.2rem; fill: none; stroke: var(--ink-faint); stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; transition: transform 200ms ease-out; }
  .form__chev.open { transform: rotate(180deg); }
  .form--link { color: inherit; text-decoration: none; display: block; }
  .form--link:hover h2 { text-decoration: underline; }
  .form--link .form__head { display: flex; }
  .form__chev--go { stroke: var(--brand); }
  .form__title h2 { margin: 0; font-size: 1.02rem; line-height: 1.2; }
  .form__count { font-size: 0.78rem; color: var(--ink-faint); }
  .form__new {
    flex: 0 0 auto; display: inline-flex; align-items: center; gap: 0.3rem;
    min-height: var(--tap); padding: 0 0.85rem; border-radius: 999px;
    background: var(--accent); color: var(--accent-ink); font-weight: 700; text-decoration: none;
    transition: transform 150ms ease-out, filter 150ms ease-out;
  }
  .form__new:hover { filter: brightness(1.06); }
  .form__new:active { transform: scale(0.97); }
  .form__new svg { width: 1rem; height: 1rem; fill: none; stroke: currentColor; stroke-width: 2.5; stroke-linecap: round; }
  .form__closed { flex: 0 0 auto; }

  .form__list { list-style: none; margin: 0; padding: 0; }
  .form__list li + li { border-top: 1px solid var(--line-soft); }
  .form__list a {
    display: flex; justify-content: space-between; align-items: center; gap: 0.6rem;
    min-height: var(--tap); padding: 0.45rem 0; color: inherit; text-decoration: none;
  }
  .form__list a:hover .form__label { text-decoration: underline; }
  .form__label { font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .form__date { flex: 0 0 auto; font-size: 0.82rem; color: var(--ink-soft); font-variant-numeric: tabular-nums; }
  .form__empty { margin: 0; color: var(--ink-faint); font-size: 0.9rem; }
  .form__all { align-self: flex-start; font-weight: 700; color: var(--brand); text-decoration: none; min-height: var(--tap); display: inline-flex; align-items: center; }

  @media (prefers-reduced-motion: reduce) {
    .form { animation: none; }
    .form__new, .details__toggle svg, .form__chev { transition: none; }
  }
</style>
